import { useCallback, useMemo, useState } from 'react';
import type { Route } from './routes';

/**
 * Навигация Mini App: стек экранов вместо URL.
 *
 * Адресной строки внутри Telegram нет, а `history.pushState` перехватывает системный жест
 * «назад» в клиентах на Android и уводит человека из приложения вместо предыдущего экрана.
 * Поэтому история своя и явная — ровно как в прототипе (`go` / `tab` / `back`).
 */
export interface Navigation {
  readonly route: Route;
  /** Вглубь: экран кладётся в стек, `back()` вернёт на текущий. */
  push(route: Route): void;
  /** Замена текущего экрана без записи в историю. */
  replace(route: Route): void;
  /** Корневая вкладка: стек обнуляется, чтобы «назад» не уводило в чужую ветку. */
  tab(route: Route): void;
  back(): void;
  readonly canGoBack: boolean;
}

export function useNavigation(initial: Route): Navigation {
  const [stack, setStack] = useState<readonly Route[]>([initial]);

  const push = useCallback((route: Route) => {
    setStack((current) => [...current, route]);
  }, []);

  const replace = useCallback((route: Route) => {
    setStack((current) => [...current.slice(0, -1), route]);
  }, []);

  const tab = useCallback((route: Route) => {
    setStack([route]);
  }, []);

  const back = useCallback(() => {
    // Последний экран не снимается: пустой стек означал бы белый экран без выхода.
    setStack((current) => (current.length > 1 ? current.slice(0, -1) : current));
  }, []);

  return useMemo(() => {
    const route = stack[stack.length - 1];
    if (!route) throw new Error('Стек навигации пуст: такого состояния быть не может');
    return { route, push, replace, tab, back, canGoBack: stack.length > 1 };
  }, [stack, push, replace, tab, back]);
}
